import React from 'react';
import { FormattedMessage } from 'react-intl';
import * as mobxReact from 'mobx-react';
import { ITheadSort, ListLayout } from '../../global/tables/table-list-layout';
import TableLoadingRow from '../../global/tables/table-loading-row';
import { Paging } from '../../global/_paging/paging-container';
import ControlledMultiSelectionDropdown from '../../global/controlled-multi-selection-dropdown/controlled-multi-selection-dropdown';
import { SelectLeftColumnIcon } from '../../global/icons/icons';
import { IOeDataOrganizationDTO } from '../../../api/oe-data-organizations.dao';
import { connectOeDataVM } from '../oe-data.context';
import { OeDataOrganizationsVM } from './oe-data-organizations.viewmodel';
import { OeDataOrganizationsListItem } from './_oe-data-organizations-list-item/oe-data-organizations-list-item';
import './oe-data-organizations.scss';
import { NO_OE_DATA_ORGANIZATIONS_MESSAGE_TEXT } from '../oe-data.text';

type OeDataOrganizationsPresentationProps = {
    vm: OeDataOrganizationsVM;
};

export const OeDataOrganizationsPresentation = mobxReact.observer(({ vm }: OeDataOrganizationsPresentationProps): JSX.Element => {
    const isLoading = vm.isFetchingOrganizations();
    const isEmpty = !vm.getOrganizations()?.length && !isLoading;
    const visibleColumns = vm.getVisibleColumns();

    const theads: ITheadSort<never>[] = [
        ...visibleColumns.map(columnId => ({
            title: vm.getColumnTitle(columnId),
        })),
        {
            title: (
                <ControlledMultiSelectionDropdown
                    entityName=''
                    onApply={vm.applyVisibleColumns}
                    withIcon={<SelectLeftColumnIcon />}
                    onFetchItems={vm.fetchColumnItems}
                    value={vm.visibleColumnItems}
                    defaultValue={vm.defaultColumnItems}
                    withoutHighlight
                    classNames='aligned-right'
                />
            ),
            width: 72,
        },
    ];

    return (
        <div className='oe-data-organizations-tab'>
            <section className='events-list fixed-list-layout'>
                <div>
                    <ListLayout sortOptions={{ theads: theads }} isDisabled={isEmpty}>
                        {(!isLoading && vm.getOrganizations())
                            ? vm.getOrganizations().map((item: IOeDataOrganizationDTO) => (
                                <OeDataOrganizationsListItem
                                    organization={item}
                                    key={item.organizationId}
                                    visibleColumnIds={visibleColumns}
                                />
                            ))
                            : <TableLoadingRow colSpan={theads.length} />
                        }
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            <FormattedMessage {...NO_OE_DATA_ORGANIZATIONS_MESSAGE_TEXT} />
                        </div>
                    }
                </div>
                {!isLoading && <Paging pagingVM={vm.getPagingVM()} />}
            </section>
        </div>
    );
});

export const OeDataOrganizations = connectOeDataVM<OeDataOrganizationsPresentationProps>(
    OeDataOrganizationsPresentation,
    vm => ({ vm: vm.getOrganizationsVM() })
);
